import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,	
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { theme } from "../../theme";
import TrueID_logo from "../../assests/TrueID-logo-white.png";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import { LeadFormAPI } from "../../services/api";
import DialogBox from "../../components/DialogBox";
import DetectionImageViewer from "../DetectionImageViewer";
import { signOut } from "firebase/auth";
import { auth } from "../../utils/FireBaseConfigurations/config";
const BACKEND_URL = process.env.BACKEND_URL || "https://imagelabs.trueid.in/backend";
const S3_BASE_URL = process.env.S3_BASE_URL || "https://s3.ap-south-1.amazonaws.com/imagelabs.trueid.in/";

const AdminPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [viewerDialogOpen, setViewerDialogOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allUserImages, setAllUserImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  
// Data management state
  const [selectedDataType, setSelectedDataType] = useState("leadForms"); // leadForms | detectionResults | registeredUsers
  const [leadForms, setLeadForms] = useState([]);
  const [detectionResults, setDetectionResults] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [authenticityFilter, setAuthenticityFilter] = useState("all"); // all | real | fraud | unknown
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedDetectionEmail, setSelectedDetectionEmail] = useState("");
  const [selectedDetectionRecords, setSelectedDetectionRecords] = useState([]);
const [dataError, setDataError] = useState("");
const [actionLoadingId, setActionLoadingId] = useState(null);
  const [leadApprovalMap, setLeadApprovalMap] = useState({});
const [contactForms, setContactForms] = useState([]);
  const buildExportRows = () => {
    const data = getCurrentData();
    if (selectedDataType === "leadForms") {
      return data.map((d) => ({
        ID: d.id,
        Name: `${d.first_name} ${d.last_name}`,
        Email: d.business_email,
        Mobile: d.mobile_no,
        Company: d.company_name,
        Role: d.role,
        CreatedAt: d.created_at,
	CreditsLeft: (d.credits_offered ?? 0) - (d.credits_used ?? 0),
      }));
    }
    if (selectedDataType === "contactForms") {
      return data.map((d) => ({
        ID: d.id,
        FullName: d.full_name,
        Email: d.email,
        Mobile: d.mobile_number,
        Message: d.message || "-",
        CreatedAt: d.created_at,
      }));
    }
    if (selectedDataType === "detectionResults") {
      return data.map((d) => {
        let isAuthentic = null;
        try {
          const resultData = JSON.parse(d.result);
          isAuthentic = resultData.is_authentic;
        } catch (e) {
          console.warn("Failed to parse result JSON:", d.result);
        }

        return {
          ID: d.id,
          ViewImage: d.image_identifier,
          ImageID: d.image_identifier,
          UserEmail: d.user_email,
	  UserCreatedAt: d.user_created_at ?? "",
          Authentic:
            isAuthentic === null ? "Unknown" : isAuthentic ? "Yes" : "No",
          Confidence: d.confidence_score ?? "",
          DetectedAt: d.detected_at ?? "",
        };
      });
    }
    // registeredUsers
    return data.map((d) => ({
      ID: d.id,
      Email: d.user_email,
      Credits: d.credits,
      CreatedAt: d.created_at,
    }));
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(",")]
      .concat(
        rows.map((r) =>
          headers
            .map((h) => {
              const val = r[h] ?? "";
              const escaped = String(val).replace(/"/g, '""');
              if (/[",\n]/.test(escaped)) return `"${escaped}"`;
              return escaped;
            })
            .join(",")
        )
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadBlob(blob, `${selectedDataType}-export-${ts}.csv`);
  };

  const exportAsExcel = async () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    try {
      const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(wb, `${selectedDataType}-export-${ts}.xlsx`);
    } catch (e) {
      // Fallback to CSV if xlsx is unavailable
      exportAsCSV();
    }
  };

  useEffect(() => {
    // Get current Firebase user
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.email) {
        setCurrentUser({ email: user.email });
        setIsLoading(false);
        fetchData();
      } else {
        // No user logged in, redirect to main page
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  // Fetch data when data type changes
  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [selectedDataType,currentUser]);

  // Fetch data based on selected type
  const fetchData = async () => {
    if (selectedDataType === "leadForms") {
      await fetchLeadForms();
    } else if (selectedDataType === "detectionResults") {
      await fetchDetectionResults();
    } else if (selectedDataType === "contactForms") {
      await fetchContactForms();
    }
  };

  // Fetch lead forms from API
  const fetchLeadForms = async () => {
    setDataLoading(true);
    setDataError("");

    try {
      const token = localStorage.getItem("access_token");
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      // Lead forms are public per backend; keep as-is
      const response = await LeadFormAPI.getLeadForms(params);

      if (response.success) {
        setLeadForms(response.data);
      } else {
        setDataError(response.message || "Failed to fetch lead forms");
      }
    } catch (error) {
      console.error("Error fetching lead forms:", error);
      setDataError("Error fetching lead forms. Please try again.");
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch detection results from API
  const fetchDetectionResults = async () => {
    setDataLoading(true);
    setDataError("");

    try {
      const token = localStorage.getItem("access_token");
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await LeadFormAPI.getDetectionResults(params);

      if (response.success) {
        setDetectionResults(response.data);
      } else {
        setDataError(response.message || "Failed to fetch detection results");
      }
    } catch (error) {
      console.error("Error fetching detection results:", error);
      setDataError("Error fetching detection results. Please try again.");
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch contact forms from API
  const fetchContactForms = async () => {
    setDataLoading(true);
    setDataError("");
 
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
 
      const response = await LeadFormAPI.getContactForms(params);
 
      if (response.success) {
        setContactForms(response.data);
      } else {
        setDataError(response.message || "Failed to fetch contact forms");
      }
    } catch (error) {
      console.error("Error fetching contact forms:", error);
      setDataError("Error fetching contact forms. Please try again.");
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch registered users
  const fetchRegisteredUsers = async () => {
    setDataLoading(true);
    setDataError("");
    try {
      const response = await LeadFormAPI.getRegisteredUsers();

      // The registered users API returns the array directly, not wrapped in a response object
      if (Array.isArray(response)) {
        setRegisteredUsers(response);
      } else if (response.value && Array.isArray(response.value)) {
        setRegisteredUsers(response.value);
      } else if (response.success) {
        setRegisteredUsers(response.data);
      } else {
        setDataError(response.message || "Failed to fetch registered users");
      }
    } catch (error) {
      setDataError("Error fetching registered users. Please try again.");
    } finally {
      setDataLoading(false);
    }
  };

  // Handle date filter changes
  const handleDateFilterChange = () => {
    setPage(0); // Reset to first page when filtering
    fetchData();
  };

  // Handle data type change
  const handleDataTypeChange = (event) => {
    setSelectedDataType(event.target.value);
    setPage(0); // Reset pagination
    setFromDate(""); // Clear date filters
    setToDate("");
    setAuthenticityFilter("all");
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current data based on selected type
  const getCurrentData = () => {
    let data = [];
    if (selectedDataType === "leadForms") {
      data = leadForms;
    } else if (selectedDataType === "detectionResults") {
      data = detectionResults;
    } else if (selectedDataType === "contactForms") {
      data = contactForms;
    } else {
      data = registeredUsers;
    }

    // Apply date filter
    if (fromDate && toDate) {
      data = data.filter((item) => {
        // For detection results, use detected_at; for others, use created_at
        const dateField =
          selectedDataType === "detectionResults" && item.detected_at
            ? item.detected_at
            : item.created_at;
 
        if (!dateField) return false;
	const itemDate = new Date(dateField);
	const from = new Date(fromDate);
        const to = new Date(toDate);
	to.setDate(to.getDate() + 1);
        return itemDate >= from && itemDate < to;
      });
    }

    // Apply authenticity filter for detection results
    if (
      selectedDataType === "detectionResults" &&
      authenticityFilter !== "all"
    ) {
      data = data.filter((item) => {
        /*let isAuthentic = null;
        try {
          const resultData = JSON.parse(item.result);
           // Check for prediction field first (string: "real" or "fake")
          if (resultData && typeof resultData.prediction === "string") {
            isAuthentic = resultData.prediction.toLowerCase();
          }
          // Fallback to is_authentic field (boolean)
          else if (resultData && typeof resultData.is_authentic === "boolean") {
            isAuthentic = resultData.is_authentic ? "real" : "fake";
          }		
        } catch (e) {
          console.warn("Failed to parse result JSON:", item.result);
        }*/
	
	const authenticity = resolveAuthenticity(item.result);

        if (authenticityFilter === "real") return authenticity === "real";
        if (authenticityFilter === "fraud" || authenticityFilter === "fake") {
          return authenticity === "fake";
        }

       // if (authenticityFilter === "real") return isAuthentic === true;
       // if (authenticityFilter === "fake") return isAuthentic === false;
	  if (authenticityFilter === "unknown") return authenticity === "unknown";    
	  return true;
      });
    }
    

    return data;
  };
 
  const resolveAuthenticity = (resultValue) => {
  if (!resultValue) return "unknown";
  try {
    const parsed =
      typeof resultValue === "string" ? JSON.parse(resultValue) : resultValue;
    if (parsed && typeof parsed.prediction === "string") {
      const normalized = parsed.prediction.trim().toLowerCase();
      if (normalized.includes("real")) {
        return "real";
      }
      if (normalized.includes("fake")) {
        return "fake";
      }
    }
    if (parsed && typeof parsed.is_authentic === "boolean") {
      return parsed.is_authentic ? "real" : "fake";
    }
  } catch (error) {
    console.warn("Failed to resolve authenticity from result:", resultValue, error);
  }
  return "unknown";
};	

  const extractPredictionValue = (resultValue) => {
    if (!resultValue) return "-";
    try {
      const parsed =
        typeof resultValue === "string" ? JSON.parse(resultValue) : resultValue;
      if (parsed && typeof parsed.prediction === "string") {
        const prediction = parsed.prediction.toLowerCase();
        return prediction.charAt(0).toUpperCase() + prediction.slice(1);
      }
      if (parsed && typeof parsed.is_authentic === "boolean") {
        return parsed.is_authentic ? "Real" : "Fake";
      }
    } catch (error) {
      console.warn("Failed to parse detection result for prediction", error);
    }
    return "-";
  };

  const buildS3ImageUrl = (record) => {
    if (!record) return null;
    const candidate = record.s3_object_key ?? record.image_identifier;
    console.log("Record data:", record);
    console.log("s3_object_key:", record.s3_object_key);
    console.log("image_identifier:", record.image_identifier);
    if (!candidate) return null;
    if (typeof candidate === "string" && /^https?:\/\//.test(candidate)) {
      return candidate;
    }
    const normalized =
      typeof candidate === "string"
        ? candidate.replace(/^\/+/, "")
        : String(candidate);
    return `${S3_BASE_URL}${normalized}`;
  };

  const openImageInNewTab = (record, allRecords = []) => {
    const imageUrl = buildS3ImageUrl(record);
    if (!imageUrl) return;
    const confidence = record?.confidence_score ?? "-";
    const resultValue = extractPredictionValue(record?.result);
    // Find the index of current record in all records
    const recordIndex = allRecords.findIndex((r) => r.id === record.id);
    setCurrentImageIndex(recordIndex >= 0 ? recordIndex : 0);
    setAllUserImages(allRecords);
   
    setSelectedImageData({
      imageUrl,
      confidence,
      result: resultValue,
    });
    setViewerDialogOpen(true);
  };
 
  const handleCloseViewerDialog = () => {
    setViewerDialogOpen(false);
    setSelectedImageData(null);
    setAllUserImages([]);
    setCurrentImageIndex(0);
  };
 
  const handleNextImage = () => {
    if (currentImageIndex < allUserImages.length - 1) {
      const nextIndex = currentImageIndex + 1;
      const nextRecord = allUserImages[nextIndex];
      setCurrentImageIndex(nextIndex);
     
      const imageUrl = buildS3ImageUrl(nextRecord);
      const confidence = nextRecord?.confidence_score ?? "-";
      const resultValue = extractPredictionValue(nextRecord?.result);
     
      setSelectedImageData({
        imageUrl,
        confidence,
        result: resultValue,
      });
    }
  };
 
  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      const prevIndex = currentImageIndex - 1;
      const prevRecord = allUserImages[prevIndex];
      setCurrentImageIndex(prevIndex);
     
      const imageUrl = buildS3ImageUrl(prevRecord);
      const confidence = prevRecord?.confidence_score ?? "-";
      const resultValue = extractPredictionValue(prevRecord?.result);
     
      setSelectedImageData({
        imageUrl,
        confidence,
        result: resultValue,
      });
    }
  };

  const handleViewImages = (userEmail, records = []) => {
    setSelectedDetectionEmail(userEmail ?? "-");
    setSelectedDetectionRecords(Array.isArray(records) ? records : []);
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedDetectionRecords([]);
    setSelectedDetectionEmail("");
  };

  const handleApproveReject = async (leadId, approved) => {
    try {
      setActionLoadingId(leadId);
      const res = await LeadFormAPI.approveLead({ leadId, approved });
      setLeadApprovalMap((prev) => ({ ...prev, [leadId]: approved }));
    } catch (e) {
      // swallow errors silently for now as alerts are removed
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleSignout = async () => {
    handleMenuClose();
    try {
      // Sign out from Firebase
      await signOut(auth);
      // Clear all localStorage
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("admin_email");
      // Reset state
      setCurrentUser(null);
      // Navigate to main page
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };
 
  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (currentUser) {
    const rawData = getCurrentData();
    const tableData =
      selectedDataType === "detectionResults"
        ? Object.values(
            rawData.reduce((acc, item) => {
              const emailKey = item.user_email || "Unknown";
              if (!acc[emailKey]) {
                acc[emailKey] = {
                  user_email: item.user_email ?? "-",
		  created_at: item.user_created_at || null,
                  credits_offered: item.credits_offered || null,
                  credits_used: item.credits_used || null,
                  images_uploaded: 0,
		  real_images: 0,
                  fake_images: 0,
                  records: [],
                };
              }
              acc[emailKey].images_uploaded += 1;
               // Count real and fake images based on prediction
              let prediction = null;
              try {
                const resultData = JSON.parse(item.result);
                if (resultData && typeof resultData.prediction === "string") {
                  prediction = resultData.prediction.toLowerCase();
                } else if (resultData && typeof resultData.is_authentic === "boolean") {
                  prediction = resultData.is_authentic ? "real" : "fake";
                }
              } catch (e) {
                console.warn("Failed to parse result JSON for counting:", item.result);
              }
             
              if (prediction === "real") {
                acc[emailKey].real_images += 1;
              } else if (prediction === "fake") {
                acc[emailKey].fake_images += 1;
              }
              acc[emailKey].records.push(item);
              return acc;
            }, {})
          )
        : rawData;

    // Admin Dashboard View
    return (
      <Grid container sx={{ p: 2 }}>
        {/* Header */}
        <Grid
          size={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pb: 1.5,
          }}
        >
          <Box
            component="img"
            src={TrueID_logo}
            alt="TrueID Logo"
            sx={{ height: "45px" }}
          />
          <Box>
            <Tooltip title="Menu">
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  backgroundColor: "#28abe2",
                  ml: 1,
                  "&:hover": { backgroundColor: "#2586af" },
                }}
              >
                <MenuIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  backgroundColor: "#fff",
                },
              }}
            >
              <MenuItem disabled>
                <AccountCircleIcon fontSize="small" />
                <Typography sx={{ ml: 1 }}>
                  {currentUser.email || "Admin"}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSignout}>
                <LogoutIcon fontSize="small" />
                <Typography sx={{ ml: 1 }}>Sign out</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Grid>

        {/* Admin Dashboard Content */}
        {/* <Grid
          container
          size={12}
          justifyContent="center"
          alignItems="flex-start"
        > */}
        <Grid size={12}>
          <Paper
            elevation={3}
            sx={{
              backgroundColor: theme.palette.background,
              padding: 3,
              borderRadius: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                }}
              >
                Admin Dashboard
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/")}
                sx={{
                  textTransform: "none",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                }}
              >
                Go to Main App
              </Button>
            </Box>

            {/* Data Type Selector */}
            <Paper
              elevation={1}
              sx={{
                padding: 1,
                mb: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                borderTop: "1px solid #ccc",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#333", fontWeight: "bold" }}
              >
                Select Data Type
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={selectedDataType}
                  onChange={handleDataTypeChange}
                >
                  <FormControlLabel
                    value="leadForms"
                    control={
                      <Radio
                        sx={{
                          color: "#1976d2",
                          "&.Mui-checked": {
                            color: "#1976d2",
                          },
                        }}
                      />
                    }
                    label="Lead Forms"
                    sx={{
                      mr: 3,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                      },
                    }}
                  />
                  <FormControlLabel
                    value="detectionResults"
                    control={
                      <Radio
                        sx={{
                          color: "#1976d2",
                          "&.Mui-checked": {
                            color: "#1976d2",
                          },
                        }}
                      />
                    }
                    label="Detection Results"
	             sx={{
                      mr: 3,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                      },
                    }}
                  />
                  <FormControlLabel
                    value="contactForms"
                    control={
                      <Radio
                        sx={{
                          color: "#1976d2",
                          "&.Mui-checked": {
                            color: "#1976d2",
                          },
                        }}
                      />
                    }
                    label="Contact Forms"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>
 
              {/* Filter Section */}
              <Typography
                variant="body1"
                sx={{ mb: 1, mt: 1, color: "#333", fontWeight: "bold" }}
              >
                Filters
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  label="From Date"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  size="small"
                  label="To Date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                {selectedDataType === "detectionResults" && (
                  <FormControl sx={{ minWidth: 100 }}>
                    <InputLabel>Authenticity</InputLabel>
                    <Select
                      size="small"
                      value={authenticityFilter}
                      label="Authenticity"
                      onChange={(e) => {
                        setAuthenticityFilter(e.target.value);
                        setPage(0);
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: "#fff",
                            backdropFilter: "none",
                          },
                        },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="real">Real</MenuItem>
                      <MenuItem value="fake">Fake</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setAuthenticityFilter("all");
                    setPage(0);
                    fetchData();
                  }}
                  disabled={dataLoading}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 3,
                  }}
                >
                  Clear Filter
                </Button>
                <Box sx={{ ml: "auto" }} />
                <Button
                  variant="outlined"
                  onClick={exportAsExcel}
                  disabled={dataLoading || tableData.length === 0}
                  sx={{ textTransform: "none", borderRadius: "8px", px: 3 }}
                >
                  Export To Excel
                </Button>
              </Box>
            </Paper>


            {/* Data Table */}
            {dataLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: theme.palette.primary.main }}
                    >
                      {selectedDataType === "leadForms" ? (
                        <>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Sl.No.
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            User Email
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Full Name
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Mobile No.
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Business Email
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Company Name
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Role
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Credits Left
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Message
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Created At
                          </TableCell>
                        </>
                       ) : selectedDataType === "contactForms" ? (
                        <>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Sl.No.
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Full Name
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Email
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Mobile Number
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Message
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Created At
                          </TableCell>
                        </>
		      ) : selectedDataType === "detectionResults" ? (
                        <>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Sl.No
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            User Email
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Created At
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Credits Offered
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Credits Used
                          </TableCell>
                          {(authenticityFilter === "all" ||
                            authenticityFilter === "real") && (
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Real Images
                            </TableCell>
                          )}
                          {(authenticityFilter === "all" ||
                            authenticityFilter === "fake") && (
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Fake Images
                            </TableCell>
                          )}
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            View Images
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            ID
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Email
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Credits
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                            Created At
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item, index) => (
                        <TableRow
                          key={
                            selectedDataType === "detectionResults"
                              ? item.user_email ?? index
                              : item.id
                          }
                          hover
                        >
                          {selectedDataType === "leadForms" ? (
                            <>
                              <TableCell>
                                {page * rowsPerPage + index + 1}
                              </TableCell>
                              <TableCell>{item.user_email ?? "-"}</TableCell>
                              <TableCell>
                                {(() => {
                                  const fallback = `${item.first_name ?? ""} ${
                                    item.last_name ?? ""
                                  }`.trim();
                                  if (item.full_name && item.full_name.trim()) {
                                    return item.full_name;
                                  }
                                  return fallback || "-";
                                })()}
                              </TableCell>
                              <TableCell>{item.mobile_no ?? "-"}</TableCell>
                              <TableCell>
                                {item.business_email ?? "-"}
                              </TableCell>
                              <TableCell>{item.company_name ?? "-"}</TableCell>
                              <TableCell>{item.role ?? "-"}</TableCell>
                              <TableCell>
                              {(() => {
                                  const offered = item.credits_offered ?? 0;
                                  const used = item.credits_used ?? 0;
                                  return offered - used;
                                })()}
				  </TableCell>
                              <TableCell>
                                {item.message ?? item.comments ?? "-"}
                              </TableCell>
                            <TableCell>
                                {item.created_at
                                  ? new Date(item.created_at).toLocaleString()
                                  : "-"}
                              </TableCell>
		           </>
		          ) : selectedDataType === "contactForms" ? (
                            <>
                              <TableCell>
                                {page * rowsPerPage + index + 1}
                              </TableCell>
                              <TableCell>{item.full_name ?? "-"}</TableCell>
                              <TableCell>{item.email ?? "-"}</TableCell>
                              <TableCell>{item.mobile_number ?? "-"}</TableCell>
                              <TableCell>{item.message ?? "-"}</TableCell>
                              <TableCell>
                                {item.created_at
                                  ? new Date(item.created_at).toLocaleString()
                                  : "-"}
                              </TableCell>
                            </>		  
                          ) : selectedDataType === "detectionResults" ? (
                            <>
                              <TableCell>
                                {page * rowsPerPage + index + 1}
                              </TableCell>
                              <TableCell>{item.user_email ?? "-"}</TableCell>
			      <TableCell>
                                {item.created_at
                                  ? new Date(item.created_at).toLocaleString()
                                  : "-"}
                              </TableCell>
                              <TableCell>{item.credits_offered ?? "-"}</TableCell>
                              <TableCell>{item.credits_used ?? "-"}</TableCell>
                              {(authenticityFilter === "all" ||
                                authenticityFilter === "real") && (
                              <TableCell>{item.real_images ?? 0}</TableCell>
					 )}
                              {(authenticityFilter === "all" ||
                                authenticityFilter === "fake") && (
                              <TableCell>{item.fake_images ?? 0}</TableCell>
				)}
			      <TableCell>
                                <Button
                                  variant="text"
                                  onClick={() =>
                                    handleViewImages(
                                      item.user_email,
                                      item.records
                                    )
                                  }
                                  sx={{ textTransform: "none" }}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{item.id}</TableCell>
                              <TableCell>{item.user_email}</TableCell>
                              <TableCell>{item.credits}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {item.created_at
                                    ? new Date(
                                        item.created_at
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
		    {selectedDataType === "detectionResults" &&
                      tableData.length > 0 && (
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Total
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          {(authenticityFilter === "all" ||
                            authenticityFilter === "real") && (
                            <TableCell sx={{ fontWeight: "bold" }}>
                              {(() => {
                                const currentPageData = tableData.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage
                                );
                                const currentPageReal = currentPageData.reduce(
                                  (sum, item) => sum + (item.real_images ?? 0),
                                  0
                                );
                                const totalReal = tableData.reduce(
                                  (sum, item) => sum + (item.real_images ?? 0),
                                  0
                                );
                                return `${currentPageReal}/${totalReal}`;
                              })()}
                            </TableCell>
                          )}
                          {(authenticityFilter === "all" ||
                            authenticityFilter === "fake") && (
                            <TableCell sx={{ fontWeight: "bold" }}>
                              {(() => {
                                const currentPageData = tableData.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage
                                );
                                const currentPageFake = currentPageData.reduce(
                                  (sum, item) => sum + (item.fake_images ?? 0),
                                  0
                                );
                                const totalFake = tableData.reduce(
                                  (sum, item) => sum + (item.fake_images ?? 0),
                                  0
                                );
                                return `${currentPageFake}/${totalFake}`;
                              })()}
                            </TableCell>
                          )}
                          <TableCell></TableCell>
                        </TableRow>
                      )}
                    {tableData.length === 0 && !dataLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            selectedDataType === "leadForms"
                              ? 10
                              : selectedDataType === "detectionResults"
                              ? authenticityFilter === "all"
                                ? 8
                                : 7
			      : selectedDataType === "contactForms"
                              ? 6	  
                              : 4
                          }
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            {selectedDataType === "leadForms"
                              ? "No lead forms found"
                              : selectedDataType === "detectionResults"
                              ? "No detection results found"
                              : selectedDataType === "contactForms"
                              ? "No contact forms found"
			      : "No registered users found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={tableData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#fff",
                        },
                      },
                    },
                  }}
                />
              </TableContainer>
            )}

            <Dialog
              open={imageDialogOpen}
              onClose={handleCloseImageDialog}
              maxWidth="md"
              fullWidth
              sx={{
                "& .MuiDialog-paper": {
                  backgroundColor: "#fff",
                  color: "#000",
                },
              }}
            >
              <DialogTitle>
                Detection Results for {selectedDetectionEmail || "-"}
              </DialogTitle>
              <DialogContent>
                {selectedDetectionRecords.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No images available for this user.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Image Identifier
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Confidence Score
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Result
                          </TableCell>
			  <TableCell sx={{ fontWeight: "bold" }}>
                            Detected At
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Image Link
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDetectionRecords.map((record) => {
                          const prediction = extractPredictionValue(
                            record.result
                          );
                          const imageUrl = buildS3ImageUrl(record);
                          const detectedAt = record.detected_at
                            ? new Date(record.detected_at).toLocaleString()
                            : "-";
			  return (
                            <TableRow
                              key={`${record.id}-${record.image_identifier}`}
                            >
                              <TableCell>
                                {record.image_identifier ?? "-"}
                              </TableCell>
                              <TableCell>
                                {record.confidence_score ?? "-"}
                              </TableCell>
                              <TableCell>{prediction}</TableCell>
                              <TableCell>{detectedAt}</TableCell>
			      <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openImageInNewTab(record, selectedDetectionRecords)}
                                  disabled={!imageUrl}
                                  sx={{ textTransform: "none" }}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseImageDialog}
                  sx={{ textTransform: "none" }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
	    <DialogBox
              open={viewerDialogOpen}
              onClose={handleCloseViewerDialog}
              maxWidth="480px"
            >
              {selectedImageData && (
                <DetectionImageViewer
                  imageUrl={selectedImageData.imageUrl}
                  confidence={selectedImageData.confidence}
                  result={selectedImageData.result}
                  onPrevious={handlePreviousImage}
                  onNext={handleNextImage}
                  hasPrevious={currentImageIndex > 0}
                  hasNext={currentImageIndex < allUserImages.length - 1}
                />
              )}
            </DialogBox>
          </Paper>
        </Grid>
        {/* </Grid> */}
      </Grid>
    );
  } 
 return null;
};

export default AdminPage;

